if (!customElements.get("sub-widget")) {
  customElements.define(
    "sub-widget",
    class SubWidget extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this.hasAttribute('data-sonic-subscription')) {
          this.initSonic();
          return;
        }
        this.init();
      }

      initSonic() {
        const requestedPlan = new URLSearchParams(window.location.search).get('selling_plan');
        const select = this.querySelector('select');
        const recurring = this.querySelector('input[value="subscription"]');
        if (recurring && Array.from(select?.options || []).some((option) => option.value === requestedPlan)) {
          select.value = requestedPlan;
          recurring.checked = true;
        }
        this.onSonicChange = () => this.syncSonic();
        this.addEventListener('change', this.onSonicChange);
        this.onSonicReady = () => this.syncSonic();
        document.addEventListener('DOMContentLoaded', this.onSonicReady, { once: true });
        this.syncSonic();
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const wrapper = this.closest('[data-section-id]');
          if (event.data.sectionId !== wrapper.dataset.sectionId) return;
          const replacement = event.data.html.querySelector('[data-sonic-subscription] .wt-subscription-widget__container');
          if (!replacement) return;
          const recurring = this.querySelector('input[value="subscription"]')?.checked;
          const previousSelect = this.querySelector('select');
          const previous = Array.from(previousSelect?.options || []).find(option => option.value === previousSelect.dataset.planId) || previousSelect?.selectedOptions[0];
          const oldPlanId = previous?.value;
          const oldPlanName = previous?.dataset.planName;
          this.querySelector('.wt-subscription-widget__container').replaceWith(replacement);
          const subscriptionRadio = this.querySelector('input[value="subscription"]');
          const oneTimeRadio = this.querySelector('input[value="one-time"]');
          if (subscriptionRadio && recurring) subscriptionRadio.checked = true;
          else if (oneTimeRadio) oneTimeRadio.checked = true;
          const select = this.querySelector('select');
          const matching = Array.from(select?.options || []).find((option) => option.value === oldPlanId || option.dataset.planName === oldPlanName);
          if (matching) select.value = matching.value;
          this.syncSonic();
          // The theme updates the buybox markup during the same variant event.
          requestAnimationFrame(() => { if (this.isConnected) this.syncSonic(); });
        });
      }

      syncSonic() {
        const container = this.querySelector('.wt-subscription-widget__container');
        const input = this.querySelector('input[name="selling_plan"]');
        if (!container || !input) return;
        const recurring = this.querySelector('input[value="subscription"]')?.checked;
        const select = this.querySelector('select');
        const selectedOption = select?.selectedOptions[0];
        if (selectedOption?.value) select.dataset.planId = selectedOption.value;
        const option = Array.from(select?.options || []).find(option => option.value && option.value === select.dataset.planId)
          || Array.from(select?.options || []).find(option => option.value);
        const usePlan = Boolean(recurring && option);
        input.value = usePlan ? option.value : '';
        input.disabled = !usePlan;
        const frequency = this.querySelector('.sonic-subscription__frequency');
        if (frequency) frequency.hidden = false;
        if (select) {
          select.disabled = !usePlan;
          select.value = usePlan ? option.value : '';
        }
        if (option) {
          this.querySelector('[data-subscription-price]').textContent = option.dataset.price;
          const compare = this.querySelector('[data-subscription-compare]');
          compare.textContent = option.dataset.compare;
          compare.hidden = Number(option.dataset.saving) <= 0;
          this.querySelector('[data-subscription-percent]').textContent = `${option.dataset.saving}%`;
          const saving = this.querySelector('[data-subscription-saving]');
          saving.textContent = (window.sonicStrings?.save_percent || "SAVE [percent]%").replaceAll("[percent]", () => option.dataset.saving);
          saving.hidden = Number(option.dataset.saving) <= 0;
        }
        this.updateURLParamsIfEnabled(input.value);
        const prices = usePlan ? option.dataset : container.dataset;
        const priceData = {
          price: prices.price, compareAtPrice: prices.compare,
          priceRaw: Number(prices.priceRaw), compareAtPriceRaw: Number(prices.compareRaw),
        };
        this.updatePriceBlock(priceData);
        this.updateStickyPriceBlock(priceData);
        this.dispatchEvent(new CustomEvent('sonic:purchase-option-change', {
          bubbles: true, detail: { variantId: container.dataset.variantId },
        }));
      }

      init() {
        this.handlePlanClick = this.handlePlanClick.bind(this);

        this.sellingPlanInput = this.querySelector("input[type='hidden']");
        this.allPlans = this.querySelectorAll(".wt-subscription-widget__plans");

        this.addEventListeners();
        this.setInitialPlan();
      }

      setInitialPlan() {
        const initialPlanSelected = this.querySelector('.wt-subscription-widget__plans--selected');
        if (initialPlanSelected) {
          this.handlePlanClick(initialPlanSelected);
        }
      }

      reinit() {
        this.sellingPlanInput = this.querySelector("input[type='hidden']");
        this.allPlans = this.querySelectorAll(".wt-subscription-widget__plans");
        this.allPlans.forEach((plan) => {
          plan.addEventListener("click", () => this.handlePlanClick(plan));
        });

        this.addSelectListeners();
        this.setInitialPlan();
      }

      addEventListeners() {
        this.allPlans.forEach((plan) => {
          plan.addEventListener("click", () => this.handlePlanClick(plan));
        });

        this.addSelectListeners();

        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (e) => {
          const wrapper = this.closest('[data-section-id]');
          const sectionId = wrapper?.dataset?.sectionId;
          const originalSectionId = wrapper?.dataset?.originalSectionId;
          if (sectionId && e.data.sectionId !== sectionId && e.data.sectionId !== originalSectionId) return;

          const parsedHTML = e.data.html.querySelector(".wt-subscription-widget__container");
          if (!parsedHTML) return;

          const currentContainer = this.querySelector('.wt-subscription-widget__container');
          if (!currentContainer) return;

          currentContainer.innerHTML = parsedHTML.innerHTML;
          this.reinit();

          if (originalSectionId) {
            const hiddenInput = this.querySelector('input[name="selling_plan"]');
            if (hiddenInput) hiddenInput.setAttribute('form', `product-form-${sectionId}`);
          }
        });
      }

      addSelectListeners() {
        this.querySelectorAll(".wt-subscription-widget__plan-content__select").forEach((select) => {
          select.addEventListener("change", (e) => {
            e.stopPropagation();
            this.handleSelectChange(select);
          });
        });
      }

      handleSelectChange(select) {
        const plan = select.closest(".wt-subscription-widget__plans");
        if (!plan || !plan.classList.contains("wt-subscription-widget__plans--selected")) return;

        const selectedOption = select.options[select.selectedIndex];
        if (!selectedOption) return;

        this.sellingPlanInput.value = selectedOption.value;

        const compareAtPrice = selectedOption.dataset.compareAtPrice;
        const price = selectedOption.dataset.price;
        this.updatePrice(plan, compareAtPrice, price);
        this.updateURLParamsIfEnabled(selectedOption.value);
        const priceData = {
          price: selectedOption.dataset.priceFormatted,
          compareAtPrice: selectedOption.dataset.compareAtPriceFormatted,
          priceRaw: Number(selectedOption.dataset.priceRaw),
          compareAtPriceRaw: Number(selectedOption.dataset.compareAtPriceRaw),
        };
        this.updatePriceBlock(priceData);
        this.updateStickyPriceBlock(priceData);
      }

      handlePlanClick(plan) {
        this.allPlans.forEach((item) => {
          item.classList.remove("wt-subscription-widget__plans--selected");
        });
        plan.classList.add("wt-subscription-widget__plans--selected");

        const selectedPlanValue = plan.querySelector("select")?.value;
        if (selectedPlanValue) {
          this.sellingPlanInput.value = selectedPlanValue;
          const selectedOption = [...plan.querySelector("select").options].find(
            (option) => option.value === selectedPlanValue
          );
          if (selectedOption) {
            const compareAtPrice = selectedOption.dataset.compareAtPrice;
            const price = selectedOption.dataset.price;
            this.updatePrice(plan, compareAtPrice, price);
            const priceData = {
              price: selectedOption.dataset.priceFormatted,
              compareAtPrice: selectedOption.dataset.compareAtPriceFormatted,
              priceRaw: Number(selectedOption.dataset.priceRaw),
              compareAtPriceRaw: Number(selectedOption.dataset.compareAtPriceRaw),
            };
            this.updatePriceBlock(priceData);
            this.updateStickyPriceBlock(priceData);
          }
        } else {
          this.sellingPlanInput.value = "";
          const priceData = {
            price: plan.dataset.priceFormatted,
            compareAtPrice: plan.dataset.compareAtPriceFormatted,
            priceRaw: Number(plan.dataset.priceRaw),
            compareAtPriceRaw: Number(plan.dataset.compareAtPriceRaw),
          };
          this.updatePriceBlock(priceData);
          this.updateStickyPriceBlock(priceData);
        }

        this.updateURLParamsIfEnabled(selectedPlanValue);
      }

      updatePrice(plan, compareAtPrice, price) {
        const priceElement = plan.querySelector('.wt-subscription-widget__plan-header__price-regular');
        const compareAtPriceElement = plan.querySelector('.wt-subscription-widget__plan-header__price-sale');

        if (compareAtPrice && compareAtPriceElement) {
          compareAtPriceElement.innerHTML = compareAtPrice;
        }
        if (priceElement) {
          if (compareAtPrice) {
            priceElement.classList.add('wt-subscription-widget__plan-header__price-regular--sale');
          } else {
            priceElement.classList.remove('wt-subscription-widget__plan-header__price-regular--sale');
          }
          if (price) {
            priceElement.innerHTML = price;
          }
        }
      }

      updatePriceBlock({ price, compareAtPrice, priceRaw, compareAtPriceRaw }) {
        const sectionId = this.closest('[data-section-id]')?.dataset?.sectionId;
        if (!sectionId) return;

        const priceBlock = document.getElementById(`price-${sectionId}`);
        if (!priceBlock) return;

        const priceContainer = priceBlock.querySelector('.price');
        if (!priceContainer) return;

        const isOnSale = compareAtPriceRaw > priceRaw;

        priceContainer.classList.toggle('price--on-sale', isOnSale);

        const regularPriceEl = priceContainer.querySelector('.price__regular .wt-product__price__final');
        if (regularPriceEl) regularPriceEl.innerHTML = price;

        const salePriceEl = priceContainer.querySelector('.price__sale .wt-product__price__final');
        if (salePriceEl) salePriceEl.innerHTML = price;

        const saleDiv = priceContainer.querySelector('.price__sale');
        if (saleDiv) {
          saleDiv.classList.toggle('visible', isOnSale);
          saleDiv.classList.remove('visible-main-product');
        }

        const compareEl = priceContainer.querySelector('.wt-product__price__compare');
        if (compareEl) {
          compareEl.innerHTML = isOnSale ? compareAtPrice : '';
          compareEl.classList.toggle('hidden', !isOnSale);
        }

        let percentEl = priceContainer.querySelector('.price-item--percent');
        if (isOnSale) {
          const percent = Math.round((compareAtPriceRaw - priceRaw) / compareAtPriceRaw * 100);
          if (percentEl) {
            percentEl.textContent = `-${percent}%`;
          } else {
            const wrapper = priceContainer.querySelector('.price__sale__details-wrapper');
            if (wrapper) {
              percentEl = document.createElement('span');
              percentEl.className = 'price-item--percent';
              percentEl.textContent = `-${percent}%`;
              wrapper.appendChild(percentEl);
            }
          }
        } else if (percentEl) {
          percentEl.remove();
        }
      }

      updateStickyPriceBlock({ price, compareAtPrice, priceRaw, compareAtPriceRaw }) {
        const sectionId = this.closest('[data-section-id]')?.dataset?.sectionId;
        if (!sectionId) return;

        const stickyPriceBlock = document.getElementById(`sticky-price-${sectionId}`);
        if (!stickyPriceBlock) return;

        const priceContainer = stickyPriceBlock.querySelector('.price');
        if (!priceContainer) return;

        const isOnSale = compareAtPriceRaw > priceRaw;

        priceContainer.classList.toggle('price--on-sale', isOnSale);

        const regularPriceEl = priceContainer.querySelector('.price__regular .wt-product__price__final');
        if (regularPriceEl) regularPriceEl.innerHTML = price;

        const salePriceEl = priceContainer.querySelector('.price__sale .wt-product__price__final');
        if (salePriceEl) salePriceEl.innerHTML = price;

        const saleDiv = priceContainer.querySelector('.price__sale');
        if (saleDiv) {
          saleDiv.classList.toggle('visible', isOnSale);
          saleDiv.classList.remove('visible-main-product');
        }

        const compareEl = priceContainer.querySelector('.wt-product__price__compare');
        if (compareEl) {
          compareEl.innerHTML = isOnSale ? compareAtPrice : '';
          compareEl.classList.toggle('hidden', !isOnSale);
        }

        let percentEl = priceContainer.querySelector('.price-item--percent');
        if (isOnSale) {
          const percent = Math.round((compareAtPriceRaw - priceRaw) / compareAtPriceRaw * 100);
          if (percentEl) {
            percentEl.textContent = `-${percent}%`;
          } else {
            const wrapper = priceContainer.querySelector('.price__sale__details-wrapper');
            if (wrapper) {
              percentEl = document.createElement('span');
              percentEl.className = 'price-item--percent';
              percentEl.textContent = `-${percent}%`;
              wrapper.appendChild(percentEl);
            }
          }
        } else if (percentEl) {
          percentEl.remove();
        }
      }

      updateURLParamsIfEnabled(planId) {
        const wrapper = this.closest('[data-update-url-on-plan-change]');
        if (wrapper?.dataset.updateUrlOnPlanChange === 'false') return;

        const urlParams = new URLSearchParams(window.location.search);
        if (planId) {
          urlParams.set("selling_plan", planId);
          if (this.hasAttribute('data-sonic-subscription')) {
            urlParams.set('variant', this.querySelector('.wt-subscription-widget__container').dataset.variantId);
          }
        } else {
          urlParams.delete("selling_plan");
        }
        if (!this.hasAttribute('data-sonic-subscription')) {
          window.history.pushState({}, "", `${window.location.pathname}?${urlParams.toString()}`);
          return;
        }
        const query = urlParams.toString();
        const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
        if (nextUrl !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
          window.history.replaceState({}, "", nextUrl);
        }
      }

      disconnectedCallback() {
        if (this.onSonicChange) this.removeEventListener('change', this.onSonicChange);
        if (this.onSonicReady) document.removeEventListener('DOMContentLoaded', this.onSonicReady);
        if (typeof this.unsubscribeVariantChange === 'function') {
          this.unsubscribeVariantChange();
        }
      }
    }
  );
}
